import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AudienceProvider } from "./context/AudienceContext";
import { WatchlistProvider } from "./context/WatchlistContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import HomePage from "./pages/HomePage";
import FacilitiesPage from "./pages/FacilitiesPage";
import FacilityDetailPage from "./pages/FacilityDetailPage";
import OwnersPage from "./pages/OwnersPage";
import OwnerDetailPage from "./pages/OwnerDetailPage";
import ChainsPage from "./pages/ChainsPage";
import ChainDetailPage from "./pages/ChainDetailPage";
import RankingsPage from "./pages/RankingsPage";
import AskAIPage from "./pages/AskAIPage";
import WatchlistPage from "./pages/WatchlistPage";
import ManagedCarePage from "./pages/ManagedCarePage";
import MAStateDetailPage from "./pages/MAStateDetailPage";
import ManagedCarePayersPage from "./pages/ManagedCarePayersPage";
import MAPayerDetailPage from "./pages/MAPayerDetailPage";
import AdminErrorsPage from "./pages/AdminErrorsPage";
import PhysiciansPage from "./pages/PhysiciansPage";
import PhysicianDetailPage from "./pages/PhysicianDetailPage";
import PhysicianIntelligencePage from "./pages/PhysicianIntelligencePage";
import HospitalsPage from "./pages/HospitalsPage";
import HospitalDetailPage from "./pages/HospitalDetailPage";
import HospitalIntelligencePage from "./pages/HospitalIntelligencePage";
import CensusIntelligencePage from "./pages/CensusIntelligencePage";
import McTargetsPage from "./pages/McTargetsPage";
import EntityIntelligencePage from "./pages/EntityIntelligencePage";
import HomeHealthPage from "./pages/HomeHealthPage";
import HomeHealthDetailPage from "./pages/HomeHealthDetailPage";
import HospicePage from "./pages/HospicePage";
import HospiceDetailPage from "./pages/HospiceDetailPage";
import ContinuumPage from "./pages/ContinuumPage";
import PricingPage from "./pages/PricingPage";
import { PaymentSuccessPage, PaymentCancelPage } from "./pages/BillingPages";
import AccountPage from "./pages/AccountPage";
import SupportPage, { SupportThanksPage } from "./pages/SupportPage";
// Auth pages — additive, do NOT replace any existing page.
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminAccessCodesPage from "./pages/AdminAccessCodesPage";
// Register the global window error reporter side-effects
import "./lib/errorReporter";
import { gaPageview } from "./lib/analytics";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AnalyticsRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    gaPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

/** Wrap any non-public route in `<Protected>` to gate it behind auth. */
const Protected = ({ children, adminOnly = false }) => (
  <ProtectedRoute adminOnly={adminOnly}>{children}</ProtectedRoute>
);

/** Smart root: shows login page to logged-out users, redirects to /home if authed. */
const AUTH_BYPASS = process.env.REACT_APP_AUTH_BYPASS === 'true';

function RootGate() {
  const { user, loading } = useAuth();
  // Showcase mode: route "/" straight to the home dashboard.
  if (AUTH_BYPASS) return <Navigate to="/home" replace />;
  if (loading) return null; // tiny no-op while AuthProvider resolves /me
  if (user) return <Navigate to="/home" replace />;
  return <LoginPage />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AudienceProvider>
            <WatchlistProvider>
              <ScrollToTop />
              <AnalyticsRouteTracker />
              <Routes>
                {/* Public — login at root + the rest of the auth screens */}
                <Route path="/" element={<RootGate />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />

                {/* The previous landing page moves to /home — protected (logged-in users only) */}
                <Route path="/home" element={<Protected><HomePage /></Protected>} />

                {/* Protected — every existing dashboard route now requires sign-in */}
                <Route path="/facilities" element={<Protected><FacilitiesPage /></Protected>} />
                <Route path="/facilities/:slug" element={<Protected><FacilityDetailPage /></Protected>} />
                <Route path="/owners" element={<Protected><OwnersPage /></Protected>} />
                <Route path="/owners/:slug" element={<Protected><OwnerDetailPage /></Protected>} />
                <Route path="/chains" element={<Protected><ChainsPage /></Protected>} />
                <Route path="/chains/:slug" element={<Protected><ChainDetailPage /></Protected>} />
                <Route path="/rankings" element={<Protected><RankingsPage /></Protected>} />
                <Route path="/ask" element={<Protected><AskAIPage /></Protected>} />
                <Route path="/watchlist" element={<Protected><WatchlistPage /></Protected>} />
                <Route path="/managed-care" element={<Protected><ManagedCarePage /></Protected>} />
                <Route path="/managed-care/state/:code" element={<Protected><MAStateDetailPage /></Protected>} />
                <Route path="/managed-care/payers" element={<Protected><ManagedCarePayersPage /></Protected>} />
                <Route path="/managed-care/payer/:org" element={<Protected><MAPayerDetailPage /></Protected>} />
                <Route path="/physicians" element={<Protected><PhysiciansPage /></Protected>} />
                <Route path="/physicians/:npi" element={<Protected><PhysicianDetailPage /></Protected>} />
                <Route path="/physician-intelligence" element={<Protected><PhysicianIntelligencePage /></Protected>} />
                <Route path="/hospitals" element={<Protected><HospitalsPage /></Protected>} />
                <Route path="/hospitals/:ccn" element={<Protected><HospitalDetailPage /></Protected>} />
                <Route path="/hospital-intelligence" element={<Protected><HospitalIntelligencePage /></Protected>} />
                <Route path="/census-intelligence" element={<Protected><CensusIntelligencePage /></Protected>} />
                <Route path="/mc-targets" element={<Protected><McTargetsPage /></Protected>} />
                <Route path="/entity-intel" element={<Protected><EntityIntelligencePage /></Protected>} />
                <Route path="/home-health" element={<Protected><HomeHealthPage /></Protected>} />
                <Route path="/home-health/:ccn" element={<Protected><HomeHealthDetailPage /></Protected>} />
                <Route path="/hospice" element={<Protected><HospicePage /></Protected>} />
                <Route path="/hospice/:ccn" element={<Protected><HospiceDetailPage /></Protected>} />
                <Route path="/continuum-of-care" element={<Protected><ContinuumPage /></Protected>} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                <Route path="/account" element={<ProtectedRoute allowUnsubscribed><AccountPage /></ProtectedRoute>} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/support/thanks" element={<SupportThanksPage />} />
                <Route path="/discover" element={<Protected><FacilitiesPage /></Protected>} />

                {/* Admin-only */}
                <Route path="/admin/errors" element={<Protected adminOnly><AdminErrorsPage /></Protected>} />
                <Route path="/admin/users" element={<Protected adminOnly><AdminUsersPage /></Protected>} />
                <Route path="/admin/access-codes" element={<Protected adminOnly><AdminAccessCodesPage /></Protected>} />
              </Routes>
            </WatchlistProvider>
          </AudienceProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
